import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { workOrdersService } from "@/services";
import { ChangeOrderDto } from "@shared/types/ChangeOrderDto";
import { GetChangeRequestsResponse } from "@shared/types/payloads/GetChangeRequestsResponse";

interface CreateChangeRequestParams {
  serviceOrderId: string;
  type: string;
  changes: string[];
}

interface ChangeRequestsContextType {
  changeRequests: GetChangeRequestsResponse[];
  pendingChanges: ChangeOrderDto[];
  rejectedChanges: ChangeOrderDto[];
  approvedChanges: ChangeOrderDto[];
  isLoading: boolean;
  error: string | null;
  fetchChangeRequests: (serviceOrderId: string) => Promise<void>;
  createChangeRequest: (params: CreateChangeRequestParams) => Promise<void>;
  acceptChangeOrder: (
    changeRequestId: string,
    changeOrderId: string
  ) => Promise<{ signingUrl?: string }>;
}

const ChangeRequestsContext = createContext<ChangeRequestsContextType | null>(
  null
);

export function ChangeRequestsProvider({ children }: { children: ReactNode }) {
  const [changeRequests, setChangeRequests] = useState<
    GetChangeRequestsResponse[]
  >([]);
  const [pendingChanges, setPendingChanges] = useState<ChangeOrderDto[]>([]);
  const [rejectedChanges, setRejectedChanges] = useState<ChangeOrderDto[]>([]);
  const [approvedChanges, setApprovedChanges] = useState<ChangeOrderDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChangeRequests = useCallback(async (serviceOrderId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await workOrdersService.getChangeRequests(serviceOrderId);
      setChangeRequests(data);

      const allChangeOrders = data.flatMap((request) => request.changeOrders);

      setPendingChanges(
        allChangeOrders.filter((order) => order.status === "pending")
      );
      setRejectedChanges(
        allChangeOrders.filter((order) => order.status === "rejected")
      );
      setApprovedChanges(
        allChangeOrders.filter((order) => order.status === "approved")
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch change requests"
      );
      setChangeRequests([]);
      setPendingChanges([]);
      setRejectedChanges([]);
      setApprovedChanges([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createChangeRequest = useCallback(
    async (_params: CreateChangeRequestParams) => {
      throw new Error("Not implemented");
    },
    []
  );

  const acceptChangeOrder = useCallback(
    async (_changeRequestId: string, _changeOrderId: string) => {
      throw new Error("Not implemented");
    },
    []
  );

  return (
    <ChangeRequestsContext.Provider
      value={{
        changeRequests,
        pendingChanges,
        rejectedChanges,
        approvedChanges,
        isLoading,
        error,
        fetchChangeRequests,
        createChangeRequest,
        acceptChangeOrder,
      }}
    >
      {children}
    </ChangeRequestsContext.Provider>
  );
}

export function useChangeRequests() {
  const context = useContext(ChangeRequestsContext);
  if (!context) {
    throw new Error(
      "useChangeRequests must be used within a ChangeRequestsProvider"
    );
  }
  return context;
}
